import {TrmzFileDTO} from "@web-libs/features/files/trmz-file/api/trmz-file.types";
import {TrmzFileDTOConverter} from "@web-libs/features/files/trmz-file/api/trmz-file-dto-converter";
import {ChatroomUpdate} from "@web-libs/features/chat/domain/chatroom.functions";
import {Chatroom} from "@web-libs/features/chat/domain/types/chatroom.type";
import {ChatMessage} from "@web-libs/features/chat/domain/types/chat-message.types";
import {ChatUser} from "@web-libs/features/chat/domain/types/chat-user.type";
import {parseSendDate} from "@web-libs/features/chat/api/dto/chat-message.parser";


export type RoomSimpleDTO = {
    insertDate: string,
    id: string;
    name: string,
    chatRoomUsers: ChatUserDTO[],
    oneToOne: boolean,
    logo: TrmzFileDTO,
    groupChat: boolean,
    firstOneToOneUser?: ChatUserInnerDTO,
    secondOneToOneUser?: ChatUserInnerDTO
}

export type ChatroomSimpleDTO = RoomSimpleDTO & {
    unreadMessages: number,
    sortDate: string,
    lastMessage: string, // Encoded in Tremaze-Format
}


export type RoomHistoryDTO = {
    senderName: string,
    senderId: string,
    sendDate: string,
    message: string // Encoded in Tremaze-Format
    avatarId: string
    messageId: string
}

export type ChatUserInnerDTO = {
    id: string,
    username: string,
    firstName: string,
    lastName: string,
    avatar: TrmzFileDTO
};

export type ChatUserDTO = {
    id: string;
    user: ChatUserInnerDTO
}

export function parseChatUserInnerDTO(chatUserInnerDTO: ChatUserInnerDTO, fileDTOConverter: TrmzFileDTOConverter): ChatUser {
    return {
        id: chatUserInnerDTO.id,
        name: chatUserInnerDTO.username,
        avatarUrl: chatUserInnerDTO.avatar ? fileDTOConverter.parseDTOToFile(chatUserInnerDTO.avatar).fileUrl : undefined
    }
}

export function parseChatUserDTO(chatUserDTO: ChatUserDTO, fileDTOConverter: TrmzFileDTOConverter): ChatUser {
    return parseChatUserInnerDTO(chatUserDTO.user, fileDTOConverter);
}

const findOtherUser = (chatUsers: ChatUserInnerDTO[], ownUser:string) => {
    return chatUsers.find(chatUser => chatUser.id !== ownUser);
}

const getUserName = (chatUser?: ChatUserInnerDTO) => {
    if(!chatUser) return "Unbekannter Nutzer";
    const user = chatUser;
    return user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : user.username;
}

const getRoomName = (chatRoom: RoomSimpleDTO, ownUser: string) => {
    if(chatRoom.name) return chatRoom.name;
    let users: ChatUserInnerDTO[];
    if(chatRoom.firstOneToOneUser && chatRoom.secondOneToOneUser) {
        users = [chatRoom.firstOneToOneUser, chatRoom.secondOneToOneUser];
    } else {
        users = chatRoom.chatRoomUsers.map(chatUser => chatUser.user);
    }
    return chatRoom.oneToOne ? getUserName(findOtherUser(users, ownUser)) : chatRoom.name;
}

const getRoomLogo = (chatRoom: RoomSimpleDTO, ownUser: string) => {
    if(chatRoom.logo) return chatRoom.logo;
    let users: ChatUserInnerDTO[];
    if(chatRoom.firstOneToOneUser && chatRoom.secondOneToOneUser) {
        users = [chatRoom.firstOneToOneUser, chatRoom.secondOneToOneUser];
    } else {
        users = chatRoom.chatRoomUsers.map(chatUser => chatUser.user);
    }
    return chatRoom.oneToOne ? findOtherUser(users, ownUser)?.avatar : chatRoom.logo;
}

export function parseChatroomSimpleDTO(chatroomSimpleDTO: ChatroomSimpleDTO, ownUser:string, fileDTOConverter: TrmzFileDTOConverter, parseMessage: (message:string) => ChatMessage): Chatroom {

    const isSingleChat = chatroomSimpleDTO.oneToOne;
    const roomName = getRoomName(chatroomSimpleDTO, ownUser);
    const roomLogoDTO = getRoomLogo(chatroomSimpleDTO, ownUser);
    const roomLogoUrl = roomLogoDTO ? fileDTOConverter.parseDTOToFile(roomLogoDTO).fileUrl : undefined;

    const users = chatroomSimpleDTO.chatRoomUsers.map(chatUserDTO => parseChatUserDTO(chatUserDTO, fileDTOConverter));

    return {
        id: chatroomSimpleDTO.id,
        name: roomName,
        type: chatroomSimpleDTO.oneToOne ? "single" : "group",
        creationDate: new Date(chatroomSimpleDTO.insertDate?.endsWith("Z") ? chatroomSimpleDTO.insertDate : chatroomSimpleDTO.insertDate+"Z"),
        messages: [],
        latestMessageFallback: chatroomSimpleDTO.lastMessage ? parseMessage(chatroomSimpleDTO.lastMessage) : undefined,
        users,
        unreadMessages: chatroomSimpleDTO.unreadMessages,
        imageUrl: roomLogoUrl,
        initialSortDate: new Date(parseSendDate(chatroomSimpleDTO.sortDate)),
        oneToOne: chatroomSimpleDTO.oneToOne,
        firstOneToOneUser: chatroomSimpleDTO.firstOneToOneUser ? parseChatUserInnerDTO(chatroomSimpleDTO.firstOneToOneUser, fileDTOConverter) : users[0],
        secondOneToOneUser: chatroomSimpleDTO.secondOneToOneUser ? parseChatUserInnerDTO(chatroomSimpleDTO.secondOneToOneUser, fileDTOConverter) : users[1],
    }
}

export function parseRoomSimpleDTO(roomSimpleDTO: RoomSimpleDTO, ownUser:string, fileDTOConverter: TrmzFileDTOConverter): ChatroomUpdate {
    const isSingleChat = roomSimpleDTO.oneToOne;
    const roomName = getRoomName(roomSimpleDTO, ownUser);
    const roomLogoDTO = getRoomLogo(roomSimpleDTO, ownUser);
    const roomLogoUrl = roomLogoDTO ? fileDTOConverter.parseDTOToFile(roomLogoDTO).fileUrl : undefined;

    const users = roomSimpleDTO.chatRoomUsers.map(chatUserDTO => parseChatUserDTO(chatUserDTO, fileDTOConverter));
    return {
        id: roomSimpleDTO.id,
        name: roomName,
        type: roomSimpleDTO.oneToOne ? "single" : "group",
        users,
        imageUrl: roomLogoUrl,
        creationDate: new Date(roomSimpleDTO.insertDate?.endsWith("Z") ? roomSimpleDTO.insertDate : roomSimpleDTO.insertDate+"Z"),
        oneToOne: roomSimpleDTO.oneToOne,
        firstOneToOneUser: roomSimpleDTO.firstOneToOneUser ? parseChatUserInnerDTO(roomSimpleDTO.firstOneToOneUser, fileDTOConverter) : users[0],
        secondOneToOneUser: roomSimpleDTO.secondOneToOneUser ? parseChatUserInnerDTO(roomSimpleDTO.secondOneToOneUser, fileDTOConverter) : users[1],
    }
}
